import type {
  OAuthErrorEvent,
  OAuthResponseEvent,
  PickerCanceledEvent,
  PickerPickedEvent,
} from '@googleworkspace/drive-picker-element';
import {createStyles, Menu as MantineMenu} from '@mantine/core';
import React, {lazy, Suspense, useCallback, useMemo, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {isNodeTesting} from 'lib/Browser';
import {
  DRIVE_SCOPE,
  getGDriveOAuthClientId,
  getGDriveProjectNumber,
} from 'notebook/notes/previews/callback-oriented-google-drive-integration/googleDriveAuth';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

import {createUploadDropdownGDrivePickerHandlers} from './CreateUploadDropdownGDrivePickerHandlers';

// Used to fetch the user's Google account email after they grant OAuth consent.
const USERINFO_SCOPE = 'https://www.googleapis.com/auth/userinfo.email';

// Matches @width-medium--dropdown from less/variables.less, the legacy ui-library Menu medium width.
const DROPDOWN_MIN_WIDTH = 300;

interface DrivePickerInlineProps {
  clientId: string;
  appId: string;
  multiselect: boolean;
  scope: string;
  prompt: '' | 'none' | 'consent' | 'select_account';
  onPicked: (e: PickerPickedEvent) => void;
  onCanceled: (e: PickerCanceledEvent) => void;
  onOauthResponse: (e: OAuthResponseEvent) => void;
  onOauthError: (e: OAuthErrorEvent) => void;
}

// @googleworkspace/drive-picker-react is ESM-only (no CJS "require" export), which causes
// Node's CJS resolver to fail during Mocha test runs. We use a lazy dynamic import gated on
// isNodeTesting so the module is never resolved in tests. This follows the same pattern as
// useRolloutFlags in LaunchDarklyUtil.ts. See also: https://nodejs.org/api/errors.html#err_package_path_not_exported
const DrivePickerLazy = isNodeTesting
  ? () => null
  : lazy(() =>
      import(/* webpackChunkName: "google-drive-picker" */ '@googleworkspace/drive-picker-react').then(
        (m) => ({
          default: function DrivePickerLoaded(props: DrivePickerInlineProps): JSX.Element {
            // multiselect is an HTML boolean attribute on the underlying web component:
            // its mere presence enables multi-select regardless of value. We must omit
            // it entirely when we don't want to enable multi-select.
            const multiselectDrivePickerProp = props.multiselect ? {multiselect: true} : {};
            return (
              <m.DrivePicker
                client-id={props.clientId}
                app-id={props.appId}
                {...multiselectDrivePickerProp}
                scope={props.scope}
                prompt={props.prompt}
                onPicked={props.onPicked}
                onCanceled={props.onCanceled}
                onOauthResponse={props.onOauthResponse}
                onOauthError={props.onOauthError}
              >
                <m.DrivePickerDocsView mode="LIST" include-folders="true" select-folder-enabled="false" />
                <m.DrivePickerDocsView
                  mode="LIST"
                  include-folders="true"
                  select-folder-enabled="false"
                  enable-drives="true"
                />
              </m.DrivePicker>
            );
          },
        })
      )
    );

interface UploadDropdownButtonProps {
  onLocalUploadClick: () => void;
  onGoogleDriveFilesReady: (files: Array<File>) => void;
  fileId: string;
  multiselect?: boolean;
  disabled?: boolean;
  // Class applied to the underlying Button. Defaults to `u-tiny-spacing-bottom`
  // which provides 5px bottom margin for stacked layouts (e.g. PreviewMixin).
  // Pass an empty string (or a different utility class) when embedding in a
  // flex-centered container like a title bar, where the extra bottom margin
  // throws off vertical alignment.
  buttonClassName?: string;
  // Size of the underlying Button. Defaults to SMALL for stacked preview
  // layouts. Use MINI inside NoteLinkedGroup title bars to match the height
  // of other header action buttons (e.g. RefreshTableButton).
  size?: ButtonSize;
  // Style of the underlying Button. Defaults to SECONDARY.
  buttonStyle?: ButtonStyle;
}

export default function UploadDropdownButton(props: UploadDropdownButtonProps): JSX.Element {
  const {
    onLocalUploadClick,
    onGoogleDriveFilesReady,
    fileId,
    multiselect = false,
    disabled = false,
    buttonClassName = 'u-tiny-spacing-bottom',
    size = ButtonSize.SMALL,
    buttonStyle = ButtonStyle.SECONDARY,
  } = props;

  const {classes} = useStyles();
  const [isPickerVisible, setIsPickerVisible] = useState(false);

  const hasGDriveConfig = Boolean(getGDriveOAuthClientId() && getGDriveProjectNumber());

  const {handleCanceled, handleOauthResponse, handleOauthError, handlePicked, resetOauthFailureFlag} =
    useMemo(
      () =>
        createUploadDropdownGDrivePickerHandlers({
          setIsPickerVisible,
          onGoogleDriveFilesReady,
          fileId,
        }),
      [onGoogleDriveFilesReady, fileId]
    );

  const handleGDriveClick = useCallback(() => {
    resetOauthFailureFlag();
    analytics.trackEvent(ELN_ANALYTICS_ACTIONS.GOOGLE_DRIVE_PICKER_OPENED, {
      fileId,
    });
    setIsPickerVisible(true);
  }, [resetOauthFailureFlag, fileId]);

  return (
    <div className={classes.dropdownContainer}>
      <MantineMenu position="bottom-start" width={DROPDOWN_MIN_WIDTH} withinPortal={false}>
        <MantineMenu.Target>
          <span>
            <Button
              buttonStyle={buttonStyle}
              size={size}
              fullWidth={true}
              className={buttonClassName}
              disabled={disabled}
              text="Upload a File"
              isDropdown={true}
              dataTestAttrs={{component: 'CallbackOrientedUploadDropdownButton', element: 'uploadButton'}}
            />
          </span>
        </MantineMenu.Target>
        <MantineMenu.Dropdown>
          <MantineMenu.Item onClick={onLocalUploadClick}>Upload from computer</MantineMenu.Item>
          {hasGDriveConfig && (
            <MantineMenu.Item onClick={handleGDriveClick}>Upload from Google Drive</MantineMenu.Item>
          )}
        </MantineMenu.Dropdown>
      </MantineMenu>
      {hasGDriveConfig && isPickerVisible && (
        <Suspense fallback={null}>
          <DrivePickerLazy
            clientId={getGDriveOAuthClientId()}
            appId={getGDriveProjectNumber()}
            multiselect={multiselect}
            scope={`${USERINFO_SCOPE} ${DRIVE_SCOPE}`}
            prompt=""
            onPicked={handlePicked}
            onCanceled={handleCanceled}
            onOauthResponse={handleOauthResponse}
            onOauthError={handleOauthError}
          />
        </Suspense>
      )}
    </div>
  );
}

const useStyles = createStyles(() => ({
  dropdownContainer: {
    maxWidth: '140px',
    width: '100%',
  },
}));
